import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from './api';

/**
 * Standart değiştirme işlemi: başarıda ilgili sorguları yeniler ve (verildiyse) bildirim gösterir;
 * hatada API'nin Türkçe/İngilizce hata başlığını gösterir. Alan hataları `error` üzerinden okunur.
 */
export function useApiMutation<TVars, TResult>(
  fn: (vars: TVars) => Promise<TResult>,
  opts: {
    invalidate?: QueryKey[];
    success?: string | ((r: TResult) => string);
    onSuccess?: (r: TResult, v: TVars) => void;
    silentFieldErrors?: boolean;
  } = {},
) {
  const qc = useQueryClient();
  return useMutation<TResult, ApiError, TVars>({
    mutationFn: fn,
    onSuccess: async (r, v) => {
      // Uzun iş başlatan işlemlerden sonra üst çubuktaki iş göstergesi hemen yenilenir (F5B-21).
      await Promise.all(
        [...(opts.invalidate ?? []), ['active-jobs']].map((k) => qc.invalidateQueries({ queryKey: k })),
      );
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(r) : opts.success);
      opts.onSuccess?.(r, v);
    },
    onError: (e) => {
      if (e.code === 'step_up_required') return; // kullanıcı doğrulamayı iptal etti
      if (opts.silentFieldErrors && e.errors.length) return;
      toast.error(e.title);
    },
  });
}

export function fieldError(err: unknown, field: string): string | undefined {
  return err instanceof ApiError ? err.fieldError(field) : undefined;
}
